import { useState } from 'react';
import { 
  Shield, 
  Building2, 
  Save,
  Eye,
  EyeOff,
  Users,
  LayoutDashboard,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { toast } from 'sonner';
import api from '../../api/clients';
import { useAuthStore } from '../../store/authStore';
import { Usuarios } from './Usuarios';
import { useDashboardVisibility } from '../../hooks/useDashboardVisibility';

type TabType = 'security' | 'usuarios' | 'company';

export function Settings() {
  const { user } = useAuthStore();
  const { isVisible: dashboardVisible, toggleVisibility: toggleDashboardVisibility } = useDashboardVisibility();
  const [activeTab, setActiveTab] = useState<TabType>('security');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [companyData, setCompanyData] = useState({
    nombre: 'MI TECNO HOGAR',
    email: 'info@mitecnohogar.com',
    telefono: '+54 11 1234-5678',
    direccion: 'San Andrés de Giles, Buenos Aires, CP 6720',
    sitioWeb: 'www.mitecnohogar.com.ar',
    cuit: '30-12345678-9',
    descripcion: 'Venta de productos electrónicos y electrodomésticos',
  });

  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      toast.success('Contraseña actualizada exitosamente');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/settings/company', companyData);
      toast.success('Datos de la empresa actualizados');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar los datos');
    } finally {
      setLoading(false);
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'security':
        return renderSecurityTab();
      case 'usuarios':
        return <Usuarios />;
      case 'company':
        return renderCompanyTab();
      default:
        return null;
    }
  };

  const renderSecurityTab = () => (
    <form onSubmit={handleChangePassword} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
        <p className="font-semibold">Cambiar contraseña</p>
        <p className="text-blue-600 mt-1">
          Tu contraseña debe tener al menos 6 caracteres.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contraseña actual *
          </label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              placeholder="Ingresa tu contraseña actual"
              required
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nueva contraseña *
          </label>
          <div className="relative">
            <Input
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              placeholder="Ingresa tu nueva contraseña"
              required
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirmar nueva contraseña *
          </label>
          <div className="relative">
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              placeholder="Confirma tu nueva contraseña"
              required
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        <Save size={18} />
        {loading ? 'Actualizando...' : 'Actualizar contraseña'}
      </Button>
    </form>
  );

  const renderDashboardVisibility = () => (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
        <LayoutDashboard size={18} className="text-gray-500" />
        Visibilidad del Dashboard
      </h3>
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">
              {dashboardVisible ? ' Dashboard visible' : ' Dashboard oculto'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {dashboardVisible 
                ? 'Los KPIs y gráficos son visibles en el Dashboard'
                : 'Los KPIs y gráficos están ocultos en el Dashboard'}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleDashboardVisibility}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              dashboardVisible ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-lg ${
                dashboardVisible ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => toggleDashboardVisibility()}
            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              dashboardVisible
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {dashboardVisible ? (
              <>
                <EyeOff size={14} />
                Ocultar ahora
              </>
            ) : (
              <>
                <Eye size={14} />
                Mostrar ahora
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderCompanyTab = () => (
    <form onSubmit={handleSaveCompany} className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
        <p className="font-semibold"> Datos del emprendimiento</p>
        <p className="text-green-600 mt-1">
          Esta información aparecerá en los PDFs, facturas y remitos.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre de la empresa *
          </label>
          <Input
            value={companyData.nombre}
            onChange={(e) => setCompanyData({ ...companyData, nombre: e.target.value })}
            placeholder="Nombre de tu empresa"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            CUIT *
          </label>
          <Input
            value={companyData.cuit}
            onChange={(e) => setCompanyData({ ...companyData, cuit: e.target.value })}
            placeholder="30-12345678-9"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <Input
            type="email"
            value={companyData.email}
            onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
            placeholder="info@tuepresa.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Teléfono *
          </label>
          <Input
            value={companyData.telefono}
            onChange={(e) => setCompanyData({ ...companyData, telefono: e.target.value })}
            placeholder="+54 11 1234-5678"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dirección *
          </label>
          <Input
            value={companyData.direccion}
            onChange={(e) => setCompanyData({ ...companyData, direccion: e.target.value })}
            placeholder="Dirección completa"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sitio Web
          </label>
          <Input
            value={companyData.sitioWeb}
            onChange={(e) => setCompanyData({ ...companyData, sitioWeb: e.target.value })}
            placeholder="www.tuepresa.com"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción
          </label>
          <textarea
            value={companyData.descripcion}
            onChange={(e) => setCompanyData({ ...companyData, descripcion: e.target.value })}
            placeholder="Breve descripción de tu emprendimiento..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="bg-green-600 hover:bg-green-700 text-white"
      >
        <Save size={18} />
        {loading ? 'Guardando...' : 'Guardar datos'}
      </Button>
    </form>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Configuración</h1>
        <p className="text-sm text-gray-500">
          Administra la configuración de tu cuenta y emprendimiento
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('security')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'security'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Shield size={16} />
          Seguridad
        </button>
        <button
          onClick={() => setActiveTab('usuarios')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'usuarios'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users size={16} />
          Usuarios
        </button>
        <button
          onClick={() => setActiveTab('company')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'company'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Building2 size={16} />
          Empresa
        </button>
      </div>

      <Card className="p-6">
        {renderTab()}
        {activeTab === 'security' && renderDashboardVisibility()}
      </Card>

      <div className="text-xs text-gray-400 border-t border-gray-200 pt-4">
        <p>Usuario: <span className="font-medium">{user?.usuario || 'No definido'}</span></p>
        <p>ID: <span className="font-medium">{user?.id || 'N/A'}</span></p>
      </div>
    </div>
  );
}